/* eslint-disable */
/**
 * FAQ Tabs — accordion + sidebar tab navigation.
 *
 * Behavior:
 *  - Click a row trigger to open/close. Only one row open at a time.
 *  - Click a sidebar category to smooth-scroll to its group, applying
 *    the section's --faq-tabs-scroll-offset so the header clears any
 *    sticky site header.
 *  - As the user scrolls, the sidebar tab matching the visible group
 *    receives `.is-active` (IntersectionObserver-based scroll spy).
 *
 * Defined as a custom element so it auto-initialises per-section instance
 * and survives Shopify section reloads in the theme editor.
 */
(function () {
  if (customElements.get('faq-tabs-section')) return;

  class FaqTabsSection extends HTMLElement {
    constructor() {
      super();
      this._cleanup = [];
    }

    connectedCallback() {
      this._scrollOffset = parseInt(this.dataset.scrollOffset, 10) || 96;
      this._buildSidebar();
      this._setupAccordion();
      this._setupSidebar();
    }

    disconnectedCallback() {
      this._cleanup.forEach((fn) => {
        try { fn(); } catch (_) {}
      });
      this._cleanup = [];
    }

    /* ---------- Sidebar build (from per-category templates) ----------
       Each FAQ Category block renders an inline <template data-faq-tabs-sidebar-template>
       containing its sidebar <li>. Cloning those into the section's <ul>
       at runtime guarantees the sidebar mirrors the rendered groups —
       even when Horizon's section.blocks iteration doesn't surface
       deeply-nested theme blocks at the section level. */
    _buildSidebar() {
      const list = this.querySelector('[data-faq-tabs-categories]');
      if (!list) return;

      const templates = this.querySelectorAll('template[data-faq-tabs-sidebar-template]');
      if (!templates.length) return;

      const frag = document.createDocumentFragment();
      templates.forEach((tpl) => {
        const node = tpl.content && tpl.content.firstElementChild
          ? tpl.content.firstElementChild.cloneNode(true)
          : null;
        if (node) frag.appendChild(node);
      });

      list.replaceChildren(frag);
    }

    /* ---------- Accordion ---------- */
    _setupAccordion() {
      const items = Array.from(this.querySelectorAll('[data-faq-tabs-item]'));
      if (!items.length) return;

      items.forEach((item) => {
        const trigger = item.querySelector('[data-faq-tabs-trigger]');
        if (!trigger) return;

        const onClick = (event) => {
          event.preventDefault();
          const willOpen = !item.classList.contains('is-open');

          const triggerScreenTop = trigger.getBoundingClientRect().top;

          items.forEach((other) => {
            other.classList.remove('is-open');
            const t = other.querySelector('[data-faq-tabs-trigger]');
            if (t) t.setAttribute('aria-expanded', 'false');
          });

          if (willOpen) {
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
          }

          const shift = trigger.getBoundingClientRect().top - triggerScreenTop;
          if (Math.abs(shift) > 0.5) window.scrollTo({ top: window.scrollY + shift, behavior: 'instant' });
        };

        trigger.addEventListener('click', onClick);
        this._cleanup.push(() => trigger.removeEventListener('click', onClick));
      });
    }

    /* ---------- Sidebar (click + scroll spy) ---------- */
    _setupSidebar() {
      const links = Array.from(this.querySelectorAll('[data-faq-tabs-link]'));
      const groups = Array.from(this.querySelectorAll('[data-faq-tabs-group]'));
      if (!links.length || !groups.length) return;

      const linkByTarget = new Map();
      links.forEach((link) => {
        const target = link.dataset.faqTabsLink;
        if (target) linkByTarget.set(target, link);

        const onLinkClick = (event) => {
          const id = link.dataset.faqTabsLink;
          if (!id) return;
          const group = this.querySelector('#' + CSS.escape(id));
          if (!group) return;

          event.preventDefault();
          this._scrollToGroup(group);
          this._setActive(id);

          // Update the URL hash without re-jumping
          if (history && typeof history.replaceState === 'function') {
            history.replaceState(null, '', '#' + id);
          }
        };

        link.addEventListener('click', onLinkClick);
        this._cleanup.push(() => link.removeEventListener('click', onLinkClick));
      });

      // Activate first by default
      const firstId = groups[0].dataset.faqTabsGroup;
      if (firstId) this._setActive(firstId);

      // Scroll spy via IntersectionObserver: pick the topmost intersecting group.
      if ('IntersectionObserver' in window) {
        const rootMarginTop = -(this._scrollOffset + 8);
        const observer = new IntersectionObserver(
          (entries) => {
            const visible = entries
              .filter((e) => e.isIntersecting)
              .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

            if (visible.length) {
              const id = visible[0].target.dataset.faqTabsGroup;
              if (id) this._setActive(id);
            }
          },
          {
            root: null,
            rootMargin: rootMarginTop + 'px 0px -55% 0px',
            threshold: 0
          }
        );

        groups.forEach((g) => observer.observe(g));
        this._cleanup.push(() => observer.disconnect());
      }

      // If the page loads with a hash, jump to it (after layout) with offset.
      if (window.location.hash) {
        const id = window.location.hash.slice(1);
        const group = this.querySelector('#' + CSS.escape(id));
        if (group) {
          // Defer one frame so layout / fonts settle.
          requestAnimationFrame(() => this._scrollToGroup(group));
          this._setActive(id);
        }
      }
    }

    _scrollToGroup(group) {
      const rect = group.getBoundingClientRect();
      const top = rect.top + window.pageYOffset - this._scrollOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    }

    _setActive(id) {
      const links = this.querySelectorAll('[data-faq-tabs-link]');
      links.forEach((link) => {
        const isActive = link.dataset.faqTabsLink === id;
        link.classList.toggle('is-active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }
  }

  customElements.define('faq-tabs-section', FaqTabsSection);
})();
